import {
  useIntl,
  MessageDescriptor,
  FormattedMessage,
  defineMessages,
} from 'react-intl';
import {view, Button, Icon} from '@sail/ui';
import {PropertyListItem} from '@sail/ui/beta';

import {LightCard, widgetLinkAction} from '@stripe-internal/conversations-ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {lock} from '@sail/icons/react/Icon';
import {BankAccountWithIcon} from '../helpers/BankAccountWithIcon';
import {CurrentMerchantSection} from '../helpers/CurrentMerchantSection';

type MessageKeys = 'heading' | 'instructions' | 'cta';

const bankMessages = defineMessages({
  heading: {
    id: 'mse_inline_data.update_bank_account.bank.heading',
    description: 'Heading displayed above the bank account info.',
    defaultMessage: 'Bank account',
  },
  instructions: {
    id: 'mse_inline_data.update_bank_account.bank.instructions',
    description: 'Heading displayed above the bank account info.',
    defaultMessage:
      'Updating your account requires additional security checks. Please visit the Dashboard to make these changes.',
  },
  cta: {
    id: 'mse_inline_data.update_bank_account.bank.cta',
    description:
      'Call to action directing users to update their account in the dashboard.',
    defaultMessage: 'Update account',
  },
}) as Record<MessageKeys, MessageDescriptor>;

const cardMessages = defineMessages({
  heading: {
    id: 'mse_inline_data.update_bank_account.debit.heading',
    description: 'Heading displayed above the debit card info.',
    defaultMessage: 'Debit card',
  },
  instructions: {
    id: 'mse_inline_data.update_bank_account.debit.instructions',
    description: 'Heading displayed above the bank account info.',
    defaultMessage:
      'Changing your card requires additional security checks. Please visit the Dashboard to make these changes.',
  },
  cta: {
    id: 'mse_inline_data.update_bank_account.debit.cta',
    description:
      'Call to action directing users to update their card in the dashboard.',
    defaultMessage: 'Change card',
  },
}) as Record<MessageKeys, MessageDescriptor>;

const messages = defineMessages({
  additionalInfo: {
    id: 'mse_inline_data.update_bank_account.additional_info',
    description: 'Additional info below the update button',
    defaultMessage: 'Additional security check required',
  },
});

export type ValueProps = {
  merchant_name: string;
  merchant_avatar: string;
  is_debit: boolean;
  bank_icon: string;
  account_name: string;
  cta_link: string;
  universal_help_new_design: boolean;
};

export const UpdateBankAccount = (props: InlineDatumReactComponentProps) => {
  const {datum} = props;
  const value = datum.value as ValueProps;

  const {
    merchant_name: merchantName,
    merchant_avatar: merchantAvatar,
    is_debit: isDebit,
    bank_icon: bankIcon,
    account_name: accountName,
    cta_link: ctaLink,
    universal_help_new_design: universalHelpNewDesign,
  } = value;

  const hardcodedCopy = isDebit ? cardMessages : bankMessages;

  const analytics = useAnalytics();
  const intl = useIntl();
  const analyticsProps = {
    ...Object.entries(hardcodedCopy).reduce((result, entry) => {
      result[entry[0]] = intl.formatMessage(entry[1]);
      return result;
    }, {} as Record<string, string>),
    ...value,
  } as Record<string, unknown>;

  analytics.viewed('uh_update_bank_account_view', analyticsProps);

  return (
    <LightCard isOuter>
      <view.div css={{gap: 'medium', stack: 'y', width: 'fill'}}>
        {!universalHelpNewDesign ? (
          <CurrentMerchantSection
            merchantName={merchantName}
            merchantAvatar={merchantAvatar}
          />
        ) : null}
        <PropertyListItem
          label={
            <view.div
              css={{font: 'label.medium.emphasized'}}
              data-testid="heading"
            >
              <FormattedMessage {...hardcodedCopy.heading} />
            </view.div>
          }
          value={
            <BankAccountWithIcon
              iconName={bankIcon}
              accountName={accountName}
            />
          }
        />
        {!universalHelpNewDesign ? (
          <view.div css={{font: 'label.small'}} data-testid="instructions">
            <FormattedMessage {...hardcodedCopy.instructions} />
          </view.div>
        ) : null}

        <view.div css={{stack: 'y', gap: 'xsmall'}}>
          <Button
            data-testid="cta-button"
            type="primary"
            css={{width: 'fill'}}
            uses={[widgetLinkAction()]}
            href={ctaLink}
            onPress={() => {
              analytics.action('uh_update_bank_account_cta_click', {
                url: ctaLink,
                ...analyticsProps,
              });
            }}
          >
            <FormattedMessage {...hardcodedCopy.cta} />
          </Button>

          {universalHelpNewDesign ? (
            <view.div
              css={{
                stack: 'x',
                gap: 'small',
                alignX: 'center',
                alignY: 'center',
              }}
            >
              <Icon icon={lock} size="xsmall" />

              <view.div
                css={{font: 'label.small', color: 'text-muted'}}
                data-testid="additional-check"
              >
                <FormattedMessage {...messages.additionalInfo} />
              </view.div>
            </view.div>
          ) : null}
        </view.div>
      </view.div>
    </LightCard>
  );
};
